import React from 'react';
import _ from 'underscore';

import withSelectorLoading from 'registry/schema-field-selector/withSelectorLoading';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {getMatchingOptionDatas} from 'ui-library/menus/MenuUtil';
import {ElementKeyboardEvent, InputChangeEvent} from 'util/Types';

export interface SelectorOptionInputProps {
  className?: string;
  defaultValue: {
    name?: string;
    schemaFieldSelectorOptionIds: Array<string>;
    tagSchemaField?: SchemaField | null;
    // Specify a single starting selection by value if schemaFieldSelectorOptionIds are unavailable
    value?: string | null;
  };
  onChange: (tag: {schemaFieldSelectorOptionIds: Array<string>; value: string}) => void;
  onKeyDown?: (e: ElementKeyboardEvent) => void;
  placeholder?: string;
  shouldAllowMultiple: boolean;
  autoFocus?: boolean;
  schemaFieldSelectorId?: string | null;
  scrollable?: boolean;
  disabled?: boolean;

  // Provided by withSelectorLoading
  schemaFieldSelector: SchemaFieldSelector;
}
interface SelectorOptionInputState {
  query: string;
}

export class SelectorOptionInput extends React.Component<SelectorOptionInputProps, SelectorOptionInputState> {
  static defaultProps = {shouldAllowMultiple: false, autoFocus: false, scrollable: true};
  state: SelectorOptionInputState = {
    query: '',
  };

  private getNonArchivedOptions(): Array<OptionData> {
    return this.props.schemaFieldSelector.schemaFieldSelectorOptions.map((option) => ({
      ...option,
      value: option.name,
    }));
  }

  private getAllOptions(): Array<OptionData> {
    return this.props.schemaFieldSelector.allSchemaFieldSelectorOptions.map((option) => ({
      ...option,
      value: option.name,
    }));
  }

  // If props.defaultValue.schemaFieldSelectorOptionIds is set, returns all matching options.
  // If not, falls back to looking for an option whose value matches props.defaultValue.value
  private getSelectedOptions(): Array<OptionData> {
    const optionsFromIds = this.props.defaultValue.schemaFieldSelectorOptionIds.map(
      (id) => _.findWhere(this.getAllOptions(), {id})!
    );
    if (optionsFromIds.length) {
      return optionsFromIds;
    }

    const optionValue = this.props.defaultValue.value;
    if (optionValue) {
      const optionFromValue = this.getAllOptions().find(
        (option) => option.value === this.props.defaultValue.value
      );
      if (optionFromValue) {
        return [optionFromValue];
      }
    }

    return [];
  }

  private getSelectedValues(): Array<OptionValue> {
    // eslint-disable-next-line no-restricted-syntax
    return _.pluck(this.getSelectedOptions(), 'value');
  }

  private handleOptionChange = (values: Array<OptionValue>): void => {
    const options = values.map((name) => _.findWhere(this.getAllOptions(), {name})!);
    this.props.onChange({
      // eslint-disable-next-line no-restricted-syntax
      schemaFieldSelectorOptionIds: _.pluck(options, 'id'),
      // eslint-disable-next-line no-restricted-syntax
      value: _.pluck(options, 'name').join(', '),
    });
  };

  private handleSelectOption = (value: OptionValue): void => {
    this.setState({query: ''});
    const currentValues = this.getSelectedValues();
    if (currentValues.includes(value)) {
      this.handleRemoveOption(value);
    } else {
      this.handleOptionChange([...currentValues, value]);
    }
  };

  private handleQueryChange = (e: InputChangeEvent): void => {
    this.setState({query: e.target.value});
  };

  private handleRemoveOption = (value: OptionValue): void => {
    // eslint-disable-next-line no-restricted-syntax
    this.handleOptionChange(_.without(this.getSelectedValues(), value));
  };

  render(): JSX.Element {
    if (!this.props.schemaFieldSelector) {
      return <div>Loading...</div>;
    }
    return (
      <ChipSelectInput
        optionDatas={getMatchingOptionDatas(this.state.query, this.getNonArchivedOptions())}
        placeholder={this.props.placeholder ?? 'Choose an option'}
        onSelect={this.handleSelectOption}
        isMulti={this.props.defaultValue.tagSchemaField?.isMulti || this.props.shouldAllowMultiple}
        autoFocus={this.props.autoFocus}
        onQueryChange={this.handleQueryChange}
        query={this.state.query}
        value={this.getSelectedValues()}
        onRemove={this.handleRemoveOption}
        scrollable={this.props.scrollable}
        disabled={this.props.disabled}
      />
    );
  }
}

const ConnectedSelectorOptionInput = withSelectorLoading(
  SelectorOptionInput,
  (props) => props.schemaFieldSelectorId ?? null
);
export default ConnectedSelectorOptionInput;
